import { ImageProps } from 'next/image';

import { AssetStoryblok, GalleryImageField } from '@monoimin/sb-types';

import { getAssetImageSrc } from '../images/getAssetImageSrc';
import { getGalleryImageSrc } from '../images/getGalleryImageSrc';
import { getAssetImageDimensions } from '../images/getAssesImageDimensions';
import { getGalleryImageDimensions } from '../images/getGalleryImageDimensions';

type RemoveIndexSignature<T> = {
  [K in keyof T as K extends string
    ? string extends K
      ? never
      : K
    : never]: T[K];
};

type Field =
  | RemoveIndexSignature<AssetStoryblok>
  | GalleryImageField
  | undefined;

/**
 * Создает свойства для изображения, которое будет использоваться с компонентом `<Image>` из Next.js
 * на основе предоставленных полей. Функция обрабатывает типы `AssetStoryblok` или
 * `GalleryImageField` и возвращает объект, совместимый с `ImageProps`, или `null`,
 * если валидные данные изображения не найдены.
 *
 * @template T - Тип поля, который расширяет `AssetStoryblok`, `GalleryImageField` или `undefined`.
 * @param args - Переменное количество аргументов, каждый из которых может быть типа `AssetStoryblok`,
 * `GalleryImageField` или `undefined`.
 *
 * @returns `null`, если валидные данные изображения не предоставлены, или объект, содержащий:
 * - `src` (string): URL изображения.
 * - `alt` (string): Альтернативный текст для изображения.
 * - `width` (number): Ширина изображения.
 * - `height` (number): Высота изображения.
 *
 * Возвращаемый объект совместим с типом `ImageProps` из Next.js.
 *
 * ### Пример
 * ```typescript
 * import createPropsForImage from './path/to/createPropsForImage';
 *
 * const imageProps = createPropsForImage(assetField, galleryField);
 *
 * if (imageProps) {
 *   return <Image {...imageProps} />;
 * } else {
 *   return <p>Нет доступных данных для изображения.</p>;
 * }
 * ```
 *
 * ### Примечания
 * - Функция отдает приоритет первому валидному полю в массиве `args`.
 * - Если поле представляет галерею, используется первое изображение из галереи.
 * - Функция интегрируется с вспомогательными функциями, такими как `getAssetImageSrc` и `getGalleryImageSrc`,
 * для вычисления URL и размеров.
 *
 * @see AssetStoryblok
 * @see GalleryImageField
 * @see ImageProps
 */
export const createPropsForImage = <T extends Field>(
  ...args: Array<T>
): null | Pick<ImageProps, 'src' | 'alt' | 'height' | 'width'> => {
  const arrayProps = args.map((data) => {
    if (!data) {
      return null;
    }
    const isGalleryField = 'images' in data && data.images.length;

    const isAssetField = 'filename' in data && Boolean(data.filename);

    if (isGalleryField) {
      const dimensions = getGalleryImageDimensions(data);
      return {
        src: getGalleryImageSrc(data),
        width: dimensions.width,
        height: dimensions.height,
        alt: data.images[0].alt || '',
      };
    }

    if (isAssetField) {
      const dimensions = getAssetImageDimensions(data);
      return {
        src: getAssetImageSrc(data),
        width: dimensions.width,
        height: dimensions.height,
        alt: data.alt || '',
      };
    }

    return null;
  });

  return arrayProps.find((item) => item && item.src) || null;
};

export default createPropsForImage;
